import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { competitionAPI, predictionAPI } from '../../api/endpoints';
import { formatXPUNT, formatTimeAgo, formatDate } from '../../utils/helpers';
import { 
  FaArrowLeft, FaUsers, FaTrophy, FaClock, FaLock, FaUnlock,
  FaCheck, FaTimes, FaPlus, FaCalendar, FaDollarSign,
  FaChartBar, FaCopy, FaShare, FaFlag
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';
import PredictionForm from './PredictionForm';
import './CompetitionDetail.css';

const CompetitionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [competition, setCompetition] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [standings, setStandings] = useState([]);
  const [joined, setJoined] = useState(false);
  const [showPredictionForm, setShowPredictionForm] = useState(false);
  const [userPredictions, setUserPredictions] = useState({});

  const fetchCompetitionData = useCallback(async () => {
    setLoading(true);
    try {
      const [compRes, predsRes, standingsRes, joinedRes] = await Promise.all([
        competitionAPI.getSingle(id),
        competitionAPI.getPredictions(id, { filter: 'all' }),
        competitionAPI.getStandings(id),
        isAuthenticated ? competitionAPI.checkJoined(id) : Promise.resolve({ data: { joined: false } }),
      ]);

      setCompetition(compRes.data);
      setPredictions(predsRes.data || []);
      setStandings(standingsRes.data || []);
      setJoined(joinedRes.data?.joined || false);
    } catch (error) {
      console.error('Failed to fetch competition:', error);
      toast.error('Failed to load competition');
      navigate('/competitions');
    } finally {
      setLoading(false);
    }
  }, [id, isAuthenticated, navigate]);

  useEffect(() => {
    fetchCompetitionData();
  }, [fetchCompetitionData]);

  const handleJoin = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to join');
      navigate('/login');
      return;
    }

    try {
      await competitionAPI.join(id);
      setJoined(true);
      toast.success('Joined competition successfully!');
      fetchCompetitionData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to join');
    }
  };

  const handlePredictionSubmit = async (data) => {
    try {
      await predictionAPI.submit({
        ...data,
        competition_id: parseInt(id),
      });
      toast.success('Prediction submitted successfully!');
      setShowPredictionForm(false);
      fetchCompetitionData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit prediction');
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: competition?.title || 'XPunters Competition',
        text: `Join the competition: ${competition?.title}`,
        url: url,
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  const isCreator = competition && user && competition.author_id === user.id;
  const canParticipate = isAuthenticated && joined && competition?.status === 'open';

  if (loading) {
    return (
      <div className="competition-detail-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!competition) {
    return (
      <div className="competition-not-found">
        <h2>Competition not found</h2>
        <Link to="/competitions" className="btn btn-primary">
          <FaArrowLeft /> Back to Competitions
        </Link>
      </div>
    );
  }

  return (
    <div className="competition-detail-page">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="competition-detail-grid">
        {/* Main Content */}
        <div className="detail-main">
          <div className="detail-card">
            {/* Header */}
            <div className="detail-header">
              <div className="detail-meta">
                <span className={`competition-status ${competition.status}`}>
                  {competition.status}
                </span>
                {competition.visibility === 'private' && (
                  <span className="detail-private"><FaLock /> Private</span>
                )}
                {competition.visibility === 'public' && (
                  <span className="detail-public"><FaUnlock /> Public</span>
                )}
                {competition.is_free ? (
                  <span className="free-badge">Free Entry</span>
                ) : (
                  <span className="price-badge">{formatXPUNT(competition.join_price)}</span>
                )}
              </div>
              <div className="detail-actions">
                <button onClick={handleShare} className="action-btn">
                  <FaShare />
                </button>
                {isCreator && (
                  <Link to={`/competitions/${id}/edit`} className="action-btn">
                    <FaFlag />
                  </Link>
                )}
              </div>
            </div>

            <h1 className="detail-title">{competition.title}</h1>
            {competition.description && (
              <p className="detail-description">{competition.description}</p>
            )}

            {/* Stats */}
            <div className="detail-stats">
              <div className="stat-item">
                <FaUsers className="stat-icon" />
                <div>
                  <span className="stat-value">{competition.players_count || 0}</span>
                  <span className="stat-label">Players</span>
                </div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <FaTrophy className="stat-icon" />
                <div>
                  <span className="stat-value">{formatXPUNT(competition.total_prize_pool || 0)}</span>
                  <span className="stat-label">Prize Pool</span>
                </div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <FaClock className="stat-icon" />
                <div>
                  <span className="stat-value">{formatTimeAgo(competition.created_at)}</span>
                  <span className="stat-label">Created</span>
                </div>
              </div>
            </div>

            {/* Join Button */}
            {!joined && competition.status === 'open' && (
              <div className="join-section">
                <button onClick={handleJoin} className="btn btn-primary btn-lg">
                  Join Competition
                </button>
                {competition.visibility === 'private' && (
                  <p className="join-hint">Private competition - you need the joining code</p>
                )}
              </div>
            )}

            {joined && competition.status === 'open' && (
              <div className="joined-badge">
                <FaCheck /> You've joined this competition
                {!showPredictionForm && (
                  <button 
                    onClick={() => setShowPredictionForm(true)}
                    className="btn btn-primary btn-sm"
                  >
                    Make Prediction
                  </button>
                )}
              </div>
            )}

            {/* Prediction Form */}
            {showPredictionForm && canParticipate && (
              <div className="prediction-form-container">
                <PredictionForm 
                  competitionId={id}
                  predictions={predictions}
                  onSubmit={handlePredictionSubmit}
                  onCancel={() => setShowPredictionForm(false)}
                />
              </div>
            )}
          </div>

          {/* Predictions List */}
          <div className="detail-card">
            <h3>📊 Predictions</h3>
            {predictions.length === 0 ? (
              <div className="empty-state-small">
                <p>No predictions available yet</p>
              </div>
            ) : (
              <div className="predictions-list">
                {predictions.map((pred) => (
                  <div key={pred.id} className="prediction-item">
                    <div className="prediction-teams">
                      <span className="team-name">{pred.home_team}</span>
                      <span className="vs">vs</span>
                      <span className="team-name">{pred.away_team}</span>
                    </div>
                    <div className="prediction-info">
                      <span className="prediction-status">
                        {pred.is_expired ? '⏰ Expired' : 
                         pred.status === 'completed' ? '✅ Completed' : 
                         '⏳ Pending'}
                      </span>
                      {pred.result && (
                        <span className="prediction-result">Result: {pred.result}</span>
                      )}
                      {pred.requires_ticket && (
                        <span className="ticket-badge">🎫 {formatXPUNT(pred.ticket_price)}</span>
                      )}
                    </div>
                    {canParticipate && !pred.is_expired && pred.status !== 'completed' && (
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => {
                          // Open prediction form for this specific prediction
                        }}
                      >
                        Predict
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Standings */}
        <div className="detail-sidebar">
          <div className="standings-card">
            <h3>
              <FaChartBar /> Standings
            </h3>
            {standings.length === 0 ? (
              <div className="empty-state-small">
                <p>No standings yet</p>
              </div>
            ) : (
              <div className="standings-list">
                {standings.slice(0, 10).map((entry) => (
                  <div key={entry.user_id} className="standing-item">
                    <span className="standing-rank">#{entry.rank}</span>
                    <span className="standing-name">{entry.name}</span>
                    <span className="standing-points">{entry.points} pts</span>
                    <span className="standing-winrate">{entry.win_rate}%</span>
                  </div>
                ))}
                {standings.length > 10 && (
                  <Link to={`/competitions/${id}/standings`} className="view-all">
                    View all {standings.length} players
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Competition Info */}
          <div className="info-card">
            <h4>ℹ️ Competition Info</h4>
            <div className="info-item">
              <span>Total Winners</span>
              <span>{competition.total_winners || 1}</span>
            </div>
            <div className="info-item">
              <span>Prize Percentage</span>
              <span>{competition.prize_percentage || 100}%</span>
            </div>
            <div className="info-item">
              <span>Created By</span>
              <span>{competition.author}</span>
            </div>
            {competition.start_date && (
              <div className="info-item">
                <span>Starts</span>
                <span>{formatDate(competition.start_date)}</span>
              </div>
            )}
            {competition.end_date && (
              <div className="info-item">
                <span>Ends</span>
                <span>{formatDate(competition.end_date)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitionDetail;